import type { GuideStep } from "./GuidedTour";

/*
 * Guides de première visite des fiches (bailleur, bien, locataire).
 * Chaque cible correspond à une ancre data-tour posée dans la fiche.
 */

export const BAILLEUR_GUIDE_ID = "bailleur";
export const BAILLEUR_GUIDE_STEPS: GuideStep[] = [
  {
    target: "[data-tour='bailleur-tab-general']",
    title: "Général",
    content:
      "Retrouvez ici la vue d'ensemble de votre profil bailleur : vos informations et vos actions rapides (export comptable, déclaration des revenus fonciers, compte rendu de gestion).",
  },
  {
    target: "[data-tour='bailleur-tab-documents']",
    title: "Documents",
    content:
      "Stockez les documents liés à ce profil bailleur : statuts de SCI, RIB, justificatifs.",
  },
  {
    target: "[data-tour='bailleur-tab-prets']",
    title: "Prêts",
    content:
      "Enregistrez vos prêts immobiliers pour suivre vos mensualités et retrouver les intérêts déductibles.",
  },
  {
    target: "[data-tour='bailleur-tab-automatisation']",
    title: "Automatisation",
    content:
      "Paramétrez ce que Qalimo fait automatiquement pour ce profil : quittances, relances et envois de documents.",
  },
  {
    target: "[data-tour='bailleur-tab-parametres']",
    title: "Paramètres",
    content:
      "Modifiez à tout moment l'identité, les coordonnées et le mode de détention de ce profil.",
  },
  {
    target: "[data-tour='bailleur-infos-persos']",
    title: "Informations personnelles",
    content:
      "C'est ici que vous complétez ou modifiez les informations de votre profil bailleur. Elles alimentent automatiquement vos baux et quittances.",
  },
];

export const BIEN_GUIDE_ID = "bien";
export const BIEN_GUIDE_STEPS: GuideStep[] = [
  {
    target: "[data-tour='bien-tab-general']",
    title: "Général",
    content:
      "Retrouvez ici la vue d'ensemble de votre bien : locataire en place, paramètres du bail et description.",
  },
  {
    target: "[data-tour='bien-tab-documents']",
    title: "Documents",
    content:
      "Stockez vos diagnostics, DPE et documents du bien. Qalimo les reprend automatiquement dans vos baux.",
  },
  {
    target: "[data-tour='bien-tab-candidats']",
    title: "Candidats",
    content:
      "Recevez et comparez les dossiers des candidats à la location pour ce bien.",
  },
  {
    target: "[data-tour='bien-tab-edl']",
    title: "État des lieux",
    content:
      "Préparez vos états des lieux d'entrée et de sortie. Ils seront repris lors de la signature avec le locataire.",
  },
  {
    target: "[data-tour='bien-tab-compta']",
    title: "Comptabilité",
    content:
      "Suivez les loyers perçus et les charges de ce bien en temps réel. Utile pour faire votre déclaration fiscale.",
  },
  {
    target: "[data-tour='bien-tab-compteurs']",
    title: "Compteurs",
    content:
      "Faites les relevés d'eau, d'électricité et de gaz à chaque entrée et sortie de locataire.",
  },
  {
    target: "[data-tour='bien-tab-entretien']",
    title: "Entretien",
    content:
      "Gardez l'historique des travaux et interventions réalisés dans le logement.",
  },
  {
    target: "[data-tour='bien-add-tenant']",
    title: "Ajoutez votre locataire",
    content:
      "C'est ici que vous rattachez un locataire à ce bien. Qalimo génère ensuite le bail et programme les quittances automatiquement.",
  },
  {
    target: "[data-tour='bien-edit-description']",
    title: "Description du bien",
    content:
      "C'est ici que vous complétez ou modifiez à tout moment les informations du bien renseignées à la création.",
  },
];

/* Comptes pro (agences / mandataires) : les orienter vers l'onglet Mandataire
 * de Mon compte dès leur arrivée sur le tableau de bord, puis mettre l'onglet
 * en avant à leur première visite de la page Mon compte. */
export const PRO_DASHBOARD_GUIDE_ID = "pro-mandataire";
export const PRO_DASHBOARD_GUIDE_STEPS: GuideStep[] = [
  {
    target: "[data-tour='header-user-menu']",
    title: "Complétez votre profil mandataire",
    content:
      "Renseignez vos informations professionnelles (identité de l'agence, carte G, honoraires) dans l'onglet Mandataire de « Mon compte », accessible depuis ce menu. Elles alimenteront vos mandats et vos documents.",
  },
];

export const PRO_COMPTE_GUIDE_ID = "pro-compte-mandataire";
export const PRO_COMPTE_GUIDE_STEPS: GuideStep[] = [
  {
    target: "[data-tour='compte-tab-mandataire']",
    title: "Onglet Mandataire",
    content:
      "C'est ici que vous renseignez votre profil mandataire : identité de l'agence, carte professionnelle, honoraires et numérotation de vos factures.",
  },
];

export const LOCATAIRE_GUIDE_ID = "locataire";
export const LOCATAIRE_GUIDE_STEPS: GuideStep[] = [
  {
    target: "[data-tour='locataire-tab-general']",
    title: "Général",
    content:
      "La synthèse de votre locataire : solde, dépôt de garantie, paramètres du bail et actions prioritaires.",
  },
  {
    target: "[data-tour='locataire-tab-paiements']",
    title: "Paiements",
    content:
      "Suivez les loyers encaissés et les éventuels retards. Qalimo pointe tout automatiquement.",
  },
  {
    target: "[data-tour='locataire-tab-documents']",
    title: "Documents",
    content:
      "Bail signé, quittances et documents partagés avec votre locataire, archivés au même endroit.",
  },
  {
    target: "[data-tour='locataire-tab-emails']",
    title: "Emails",
    content:
      "L'historique de tous les emails envoyés à votre locataire, automatiques comme manuels.",
  },
  {
    target: "[data-tour='locataire-tab-actions']",
    title: "Actions",
    content:
      "Vos actions rapides : générer une quittance, envoyer une relance, préparer la fin de bail.",
  },
  {
    target: "[data-tour='locataire-tab-parametres']",
    title: "Paramètres",
    content:
      "Réglez le bail et les automatisations propres à ce locataire (quittances, relances, révision).",
  },
  {
    target: "[data-tour='locataire-inviter']",
    title: "Invitez votre locataire",
    content:
      "Invitez votre locataire à créer gratuitement son espace Qalimo. Il y retrouve ses documents : quittances, bail, état des lieux.",
  },
];
